/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useEffect, useRef, useState } from 'react';
import { generateSpeech } from '../services/geminiService';
import { generateCharacterVisual } from '../services/imageGenerators';
import { getGeminiKey } from '../utils/runtimeKeys';
import { getImageModels, getModelById, ModelDefinition } from '../services/modelRegistry';
import { Character, CharacterImage, ImageFile } from '../types';
import { LocalFolder } from '../types/project'; // v3
import { FolderTree } from './FolderTree'; // v3
import {
  CheckIcon,
  PlusIcon,
  SparklesIcon,
  Trash2Icon,
  UsersIcon,
  WaveformIcon,
  XMarkIcon,
} from './icons';
import { fileToBase64 } from './PromptForm';

const PREDEFINED_VIEWS = ['Vue de Face', 'Vue de Profil', 'Vue de Dos'];
const PREDEFINED_VOICES = ['Kore', 'Puck', 'Zephyr', 'Charon', 'Fenrir'];

interface CharacterManagerProps {
  isOpen: boolean;
  onClose: () => void;
  characters: Character[];
  onSaveCharacter: (character: Omit<Character, 'id'> & { id?: string }) => void;
  onDeleteCharacter: (characterId: string) => void;
  onUseCharacter: (character: Character) => void;
  // v3: Folder Props
  folders?: LocalFolder[];
  onCreateFolder?: (name: string, parentId?: string | null) => void;
  onDeleteFolder?: (folderId: string) => void;
}

// --- Audio Helper Functions ---

/**
 * Decodes a base64 string into a Uint8Array.
 */
function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Creates a WAV file Blob from raw PCM audio data.
 * @param pcmData The raw audio data.
 * @returns A Blob representing a playable WAV file.
 */
function createWavBlob(pcmData: Uint8Array): Blob {
  const sampleRate = 24000; // Gemini TTS sample rate
  const numChannels = 1;
  const bitsPerSample = 16;

  const dataSize = pcmData.length;
  const blockAlign = (numChannels * bitsPerSample) / 8;
  const byteRate = sampleRate * blockAlign;

  // 44 bytes for the WAV header
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  function writeString(view: DataView, offset: number, string: string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  // "fmt " sub-chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // Audio format (1 for PCM)
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  // "data" sub-chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Write PCM data
  new Uint8Array(buffer, 44).set(pcmData);

  return new Blob([view], { type: 'audio/wav' });
}

const CharacterManager: React.FC<CharacterManagerProps> = ({
  isOpen,
  onClose,
  characters,
  onSaveCharacter,
  onDeleteCharacter,
  onUseCharacter,
  // v3
  folders = [],
  onCreateFolder = () => { },
  onDeleteFolder = () => { },
}) => {
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(
    null,
  );
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null); // v3
  const [characterFolderId, setCharacterFolderId] = useState<string | undefined>(undefined); // For the edit form
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<CharacterImage[]>([]);
  const [imageLoadingStates, setImageLoadingStates] = useState<
    Record<string, boolean>
  >({});
  const [isGeneratingTrio, setIsGeneratingTrio] = useState(false);
  const [voiceName, setVoiceName] = useState(PREDEFINED_VOICES[0]);
  const [sampleText, setSampleText] = useState(
    "Bonjour, c'est un test pour ma voix.",
  );
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);

  // Model Selection (Dynamic)
  const availableModels = getImageModels();
  const [selectedModelId, setSelectedModelId] = useState<string>(availableModels[0]?.id || 'nano_banana_pro');

  const [previewImage, setPreviewImage] = useState<CharacterImage | null>(null);

  const fileInputsRef = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (isOpen && selectedCharacter) {
      setName(selectedCharacter.name);
      setDescription(selectedCharacter.description);
      setImages(selectedCharacter.images);
      setVoiceName(selectedCharacter.voiceName ?? PREDEFINED_VOICES[0]);
      setSampleText(
        selectedCharacter.sampleText ??
        "Bonjour, c'est un test pour ma voix.",
      );
      setCharacterFolderId(selectedCharacter.folderId);
      setAudioUrl(null); // Reset audio player on character change
    } else {
      setName('');
      setDescription('');
      setImages([]);
      setVoiceName(PREDEFINED_VOICES[0]);
      setSampleText("Bonjour, c'est un test pour ma voix.");
      setCharacterFolderId(selectedFolderId || undefined); // Default to current folder
      setAudioUrl(null);
    }
  }, [isOpen, selectedCharacter, selectedFolderId]);

  // Effect to clean up the generated audio object URL to prevent memory leaks.
  useEffect(() => {
    const currentUrl = audioUrl;
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [audioUrl]);

  const handleSelectCharacter = (character: Character) => {
    setSelectedCharacter(character);
  };

  // Handle drag & drop of character to folder
  const handleDropCharacterToFolder = (characterId: string, targetFolderId: string | null) => {
    const character = characters.find(c => c.id === characterId);
    if (!character) return;

    // Optimistic update: save character with new folderId
    onSaveCharacter({
      ...character,
      folderId: targetFolderId || undefined,
    });

    console.log(`[CharacterManager] Moved "${character.name}" to folder: ${targetFolderId || 'root'}`);
  };

  const handleNewCharacter = () => {
    setSelectedCharacter(null);
    setName('Nouveau Personnage');
    setDescription('');
    setImages([]);
    setVoiceName(PREDEFINED_VOICES[0]);
    setSampleText("Bonjour, c'est un test pour ma voix.");
    setCharacterFolderId(selectedFolderId || undefined);
    setAudioUrl(null);
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert('Le nom du personnage ne peut pas être vide.');
      return;
    }
    onSaveCharacter({
      id: selectedCharacter?.id,
      name,
      description,
      images,
      voiceName,
      sampleText,
      folderId: characterFolderId, // Use the form state, not just the selected tree folder
    });
    if (!selectedCharacter) {
      handleNewCharacter();
    }
  };

  const handleDelete = () => {
    if (
      selectedCharacter &&
      confirm(`Êtes-vous sûr de vouloir supprimer "${selectedCharacter.name}"?`)
    ) {
      onDeleteCharacter(selectedCharacter.id);
      setSelectedCharacter(null);
    }
  };

  const handleGenerateImage = async (viewLabel: string) => {
    setImageLoadingStates((prev) => ({ ...prev, [viewLabel]: true }));
    try {
      const contextImagesForGeneration = images
        .filter((img) => img.label !== viewLabel)
        .map((img) => {
          const byteString = atob(img.base64);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: img.type });
          return {
            file: new File([blob], img.name, { type: img.type }),
            base64: img.base64,
          };
        });

      let prompt;
      if (contextImagesForGeneration.length === 0) {
        prompt = `Generate a high-quality photorealistic FULL BODY portrait of "${name}" (${description}) for a "${viewLabel}". The character must be fully visible from head to toe in a VERTICAL PORTRAIT orientation. Centered wide shot, neutral background. Portrait aspect ratio.`;
      } else {
        prompt = `Based on the provided reference images for "${name}" (${description}), generate a new photorealistic FULL BODY portrait of the same character in "${viewLabel}". The new image must show the character fully visible from head to toe in a VERTICAL PORTRAIT orientation. Consistency in body type, hair, and clothing is absolutely critical. Single image, no grid. Portrait aspect ratio.`;
      }

      const newImageFile = await generateCharacterVisual(
        prompt,
        contextImagesForGeneration,
        null,
        selectedModelId,
        getGeminiKey() ?? undefined
      );

      const newCharImage: CharacterImage = {
        name: newImageFile.file.name,
        type: newImageFile.file.type,
        base64: newImageFile.base64,
        label: viewLabel,
      };

      setImages((prev) => {
        const existingIndex = prev.findIndex((img) => img.label === viewLabel);
        if (existingIndex > -1) {
          const updated = [...prev];
          updated[existingIndex] = newCharImage;
          return updated;
        }
        return [...prev, newCharImage];
      });
    } catch (error) {
      const isAuthError = (error as any).message?.includes('401') || (error as any).status === 401 || (error as any).message?.includes('API_KEY');
      const message = isAuthError
        ? "⚠️ Échec de la génération : Clé API manquante ou invalide.\nVeuillez configurer votre clé Gemini dans les paramètres."
        : `Impossible de générer l'image pour ${viewLabel}: ${error instanceof Error ? error.message : 'Unknown error'}`;

      alert(message);
    } finally {
      setImageLoadingStates((prev) => ({ ...prev, [viewLabel]: false }));
    }
  };

  const handleGenerateTrio = async () => {
    if (!name || !description) {
      alert("Veuillez renseigner un nom et une description avant de générer.");
      return;
    }

    setIsGeneratingTrio(true);
    const allLoading = PREDEFINED_VIEWS.reduce((acc, view) => ({ ...acc, [view]: true }), {});
    setImageLoadingStates(allLoading);

    const basePrompt = `Cinematic full body photo of ${name}. Description: ${description}. Style: Photorealistic, 8k, highly detailed, neutral grey studio background. Consistency is key.`;
    let frontImageFile: { file: File; base64: string } | null = null;

    // Step 1: Generate Vue de Face (THE ANCHOR)
    try {
      const frontLabel = 'Vue de Face';
      const frontPrompt = `${basePrompt} Full body portrait, Front View, entire body visible from head to toe, centered. Portrait aspect ratio. Vertical orientation.`;

      console.log('[CharacterManager] Generating Front View...');
      frontImageFile = await generateCharacterVisual(frontPrompt, [], null, selectedModelId, getGeminiKey() ?? undefined);
      console.log('[CharacterManager] Front View complete.');

      const frontCharacterImage: CharacterImage = {
        name: frontImageFile.file.name,
        type: frontImageFile.file.type,
        base64: frontImageFile.base64,
        label: frontLabel,
        generatedBy: selectedModelId,
      };

      setImages((prev) => {
        const others = prev.filter(img => img.label !== frontLabel);
        return [...others, frontCharacterImage];
      });
      setImageLoadingStates(prev => ({ ...prev, [frontLabel]: false }));
    } catch (error: any) {
      console.error("[CharacterManager] Failed to generate Front View:", error);

      const isAuthError = error.message?.includes('401') || error.status === 401 || error.message?.includes('API_KEY');
      const message = isAuthError
        ? "⚠️ Échec de la génération : Clé API manquante ou invalide.\nVeuillez configurer votre clé Gemini dans les paramètres."
        : "Erreur lors de la génération de la vue de face. Arrêt du pack.";

      alert(message);
      setIsGeneratingTrio(false);
      setImageLoadingStates({});
      return; // Abort if anchor fails
    }

    // Step 2 & 3: Generate Profile and Back using Front as reference
    const remainingViews = ['Vue de Profil', 'Vue de Dos'];

    for (const viewLabel of remainingViews) {
      try {
        let angleInstruction = "";
        if (viewLabel === 'Vue de Profil') angleInstruction = "Full body portrait, Side Profile View, entire body visible from head to toe, centered. Portrait aspect ratio. Vertical orientation.";
        if (viewLabel === 'Vue de Dos') angleInstruction = "Full body portrait, Back View, entire body visible from head to toe, centered. Portrait aspect ratio. Vertical orientation.";

        const prompt = `${basePrompt} ${angleInstruction} The character MUST look exactly like the reference image provided. Same clothes, same hair, same body type.`;

        console.log(`[CharacterManager] Generating ${viewLabel}...`);
        const newImageFile = await generateCharacterVisual(prompt, [frontImageFile!], null, selectedModelId, getGeminiKey() ?? undefined);
        console.log(`[CharacterManager] ${viewLabel} complete.`);

        const newCharImage: CharacterImage = {
          name: newImageFile.file.name,
          type: newImageFile.file.type,
          base64: newImageFile.base64,
          label: viewLabel,
          generatedBy: selectedModelId,
        };

        setImages((prev) => {
          const others = prev.filter(img => img.label !== viewLabel);
          return [...others, newCharImage];
        });
      } catch (error) {
        console.error(`[CharacterManager] Failed to generate ${viewLabel}:`, error);
        // Don't alert for each one, just log and continue to the next view
      } finally {
        setImageLoadingStates(prev => ({ ...prev, [viewLabel]: false }));
      }
    }

    setIsGeneratingTrio(false);
  };

  const handleImageUpload = (imageFile: ImageFile, viewLabel: string) => {
    const newCharImage: CharacterImage = {
      name: imageFile.file.name,
      type: imageFile.file.type,
      base64: imageFile.base64,
      label: viewLabel,
    };
    setImages((prev) => {
      const existingIndex = prev.findIndex((img) => img.label === viewLabel);
      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex] = newCharImage;
        return updated;
      }
      return [...prev, newCharImage];
    });
  };

  const handleGenerateVoice = async () => {
    if (!sampleText.trim() || !voiceName) return;
    setIsGeneratingAudio(true);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl); // Clean up previous URL
    }
    setAudioUrl(null);
    try {
      const audioBase64 = await generateSpeech(sampleText, voiceName);
      const pcmData = decode(audioBase64);
      const wavBlob = createWavBlob(pcmData);
      const url = URL.createObjectURL(wavBlob);
      setAudioUrl(url);
    } catch (error: any) {
      console.error('Erreur lors de la génération de la voix:', error);

      const isAuthError = error.message?.includes('401') || error.status === 401 || error.message?.includes('API_KEY');
      const message = isAuthError
        ? "⚠️ Échec de la génération vocale : Clé API manquante ou invalide.\nVeuillez configurer votre clé Gemini dans les paramètres."
        : `Impossible de générer la voix: ${error instanceof Error ? error.message : 'Unknown error'}`;

      alert(message);
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    viewLabel: string,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const imageFile = await fileToBase64<ImageFile>(file);
        handleImageUpload(imageFile, viewLabel);
      } catch (error) {
        console.error('Erreur de conversion de fichier:', error);
      }
    }
    if (e.target) e.target.value = '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        <header className="flex justify-between items-center p-4 border-b border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <UsersIcon className="w-6 h-6 text-indigo-400" />
            Character Library
          </h2>
          <button
            onClick={onClose}
            title="Close"
            className="p-1.5 rounded-full hover:bg-gray-700 text-gray-400">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </header>

        <div className="flex-grow flex overflow-hidden">
          {/* Sidebar */}
          <aside className="w-1/3 max-w-xs bg-gray-900/50 flex flex-col border-r border-gray-700">
            {/* v3: Folder Tree */}
            <div className="flex-shrink-0 h-1/3 min-h-[150px] border-b border-gray-700 overflow-hidden">
              <FolderTree
                folders={folders}
                selectedFolderId={selectedFolderId}
                onSelectFolder={setSelectedFolderId}
                onCreateFolder={onCreateFolder}
                onDeleteFolder={onDeleteFolder}
                onDropItem={handleDropCharacterToFolder}
                acceptDropType="character"
                className="h-full"
              />
            </div>

            {/* Character List (Filtered) */}
            <div className="flex-grow flex flex-col overflow-hidden p-4">
              <button
                onClick={handleNewCharacter}
                title="Create a new character from scratch"
                className="w-full flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2 mb-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors">
                <PlusIcon className="w-5 h-5" />
                Nouveau Personnage
              </button>
              <div className="overflow-y-auto flex-grow">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {selectedFolderId
                    ? folders.find(f => f.id === selectedFolderId)?.name || 'Dossier'
                    : 'Tous les personnages'}
                </h4>
                <ul className="space-y-2">
                  {characters
                    .filter(c => !selectedFolderId || c.folderId === selectedFolderId)
                    .map((character) => {
                      const thumbnail = character.images?.[0]?.base64;
                      const initials = character.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                      return (
                        <li
                          key={character.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('application/x-character', character.id);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          className="cursor-grab active:cursor-grabbing"
                        >
                          <button
                            onClick={() => handleSelectCharacter(character)}
                            title={`Edit ${character.name} - Glissez vers un dossier pour déplacer`}
                            className={`w-full text-left p-2 rounded-lg flex items-center gap-3 transition-colors ${selectedCharacter?.id === character.id
                              ? 'bg-indigo-600/30'
                              : 'hover:bg-gray-700'
                              }`}>
                            {/* Thumbnail */}
                            {thumbnail ? (
                              <img
                                src={`data:image/jpeg;base64,${thumbnail}`}
                                alt={character.name}
                                className="w-10 h-10 rounded-full object-cover border-2 border-gray-600 flex-shrink-0 pointer-events-none"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center text-gray-400 text-sm font-bold flex-shrink-0 pointer-events-none">
                                {initials || '?'}
                              </div>
                            )}
                            <span className="font-medium text-gray-200 truncate pointer-events-none">
                              {character.name}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  {characters.filter(c => !selectedFolderId || c.folderId === selectedFolderId).length === 0 && (
                    <li className="text-center text-gray-500 text-sm py-4 italic">
                      Aucun personnage ici.
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </aside>

          {/* Main Editor */}
          <main className="flex-grow p-6 flex flex-col gap-4 overflow-y-auto">
            {selectedCharacter === undefined ? (
              <div className="flex-grow flex items-center justify-center text-center text-gray-500">
                <div>
                  <h3 className="text-2xl">Sélectionnez un personnage</h3>
                  <p>Ou créez-en un nouveau pour commencer.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-start gap-4">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nom du Personnage"
                    className="flex-grow bg-transparent text-2xl font-bold text-white focus:outline-none"
                    title="Character Name"
                  />
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <select
                      value={characterFolderId || ''}
                      onChange={(e) => setCharacterFolderId(e.target.value || undefined)}
                      className="bg-gray-700 text-white text-sm p-2 rounded-lg border border-gray-600 focus:outline-none focus:border-indigo-500 max-w-[150px]"
                      title="Dossier du personnage"
                    >
                      <option value="">(Aucun dossier)</option>
                      {folders.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>

                    {selectedCharacter && (
                      <button
                        onClick={() => onUseCharacter(selectedCharacter)}
                        title="Inject this character into the Prompt Assistant"
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors">
                        <CheckIcon className="w-5 h-5" />
                        Utiliser le Personnage
                      </button>
                    )}
                    <button
                      onClick={handleSave}
                      title="Save changes to this character"
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors">
                      Sauvegarder
                    </button>
                    {selectedCharacter && (
                      <button
                        onClick={handleDelete}
                        className="p-2.5 bg-red-800/80 hover:bg-red-700 text-white rounded-lg transition-colors"
                        title="Supprimer le Personnage">
                        <Trash2Icon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="char-desc"
                    className="text-sm font-semibold mb-2 text-gray-400 block">
                    Description / Mots-clés
                  </label>
                  <textarea
                    id="char-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Ex: yeux verts, cicatrice sur le sourcil gauche, porte une veste en cuir..."
                    className="w-full h-24 bg-[#1f1f1f] border border-gray-600 rounded-lg p-3 resize-y focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                    title="Describe the character's key visual features for the AI."
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-semibold text-gray-400">
                      Images de Référence
                    </h3>
                    <button
                      onClick={handleGenerateTrio}
                      disabled={isGeneratingTrio || !name || !description}
                      className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                      {isGeneratingTrio ? (
                        <>
                          <div className="w-3 h-3 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                          Génération 3.0...
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="w-3 h-3" />
                          Générer Pack Complet (Banana Pro)
                        </>
                      )}
                    </button>
                  </div>

                  <div className="mb-4 bg-[#1f1f1f] p-2 rounded-lg border border-gray-600 flex items-center justify-between">
                    <label className="text-xs font-semibold text-gray-400">Modèle de Génération :</label>
                    <select
                      value={selectedModelId}
                      onChange={(e) => setSelectedModelId(e.target.value)}
                      className="bg-gray-700 text-white text-xs p-1.5 rounded border border-gray-500 focus:outline-none focus:border-indigo-500 max-w-[200px]"
                    >
                      {availableModels.map(model => (
                        <option key={model.id} value={model.id}>
                          {model.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-3 gap-6 p-4 bg-[#1f1f1f] border border-gray-600 rounded-lg relative">
                    {isGeneratingTrio && (
                      <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px] z-10 rounded-lg pointer-events-none" />
                    )}
                    {PREDEFINED_VIEWS.map((view) => {
                      const image = images.find((i) => i.label === view);
                      const isLoading = imageLoadingStates[view];
                      return (
                        <div
                          key={view}
                          className="flex flex-col gap-2 items-center z-0">
                          <h4 className="text-xs font-bold uppercase text-gray-500">
                            {view}
                          </h4>
                          <div className="w-56 h-80 bg-gray-700/50 rounded-md flex items-center justify-center relative p-1 border border-gray-700 overflow-hidden shadow-inner group">
                            {image && !isLoading && (
                              <>
                                <img
                                  src={`data:${image.type};base64,${image.base64}`}
                                  alt={view}
                                  onClick={() => setPreviewImage(image)}
                                  className="w-full h-full object-cover rounded-sm cursor-zoom-in group-hover:scale-[1.02] transition-transform duration-300"
                                />
                                {/* Model Badge */}
                                {image.generatedBy && (
                                  <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/70 text-[9px] font-medium text-gray-300 rounded backdrop-blur-sm">
                                    {getModelById(image.generatedBy)?.label?.replace(/^[^\s]+\s/, '') || image.generatedBy}
                                  </div>
                                )}
                              </>
                            )}
                            {isLoading && (
                              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800/80">
                                <div className="w-8 h-8 border-4 border-t-transparent border-indigo-500 rounded-full animate-spin mb-2"></div>
                                <span className="text-[10px] text-indigo-300 animate-pulse">Rendering...</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                fileInputsRef.current[view]?.click()
                              }
                              disabled={isLoading}
                              title={`Upload an image for ${view}`}
                              className="text-xs px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors disabled:opacity-50">
                              Uploader
                            </button>
                            <input
                              type="file"
                              ref={(el) => { fileInputsRef.current[view] = el; }}
                              onChange={(e) => handleFileChange(e, view)}
                              accept="image/*"
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={() => handleGenerateImage(view)}
                              disabled={isLoading}
                              title={`Generate an image for ${view} using AI`}
                              className="text-xs px-3 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors disabled:opacity-50">
                              Générer
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Voice Identity Section */}
                <div className="mt-2">
                  <h3 className="text-sm font-semibold mb-2 text-gray-400 flex items-center gap-2">
                    <WaveformIcon className="w-4 h-4" />
                    Identité Vocale
                  </h3>
                  <div className="p-4 bg-[#1f1f1f] border border-gray-600 rounded-lg flex flex-col md:flex-row gap-4 items-center">
                    <div className="flex-1 w-full">
                      <label
                        htmlFor="voice-select"
                        className="text-xs font-medium text-gray-500 block mb-1">
                        Voix
                      </label>
                      <select
                        id="voice-select"
                        value={voiceName}
                        onChange={(e) => setVoiceName(e.target.value)}
                        className="w-full bg-gray-700 p-2 rounded-md border border-gray-500"
                        title="Select a pre-built AI voice">
                        {PREDEFINED_VOICES.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1 w-full">
                      <label
                        htmlFor="sample-text"
                        className="text-xs font-medium text-gray-500 block mb-1">
                        Texte d'échantillon
                      </label>
                      <textarea
                        id="sample-text"
                        value={sampleText}
                        onChange={(e) => setSampleText(e.target.value)}
                        className="w-full h-12 bg-gray-700 p-2 rounded-md resize-none border border-gray-500"
                        title="Text for the AI to speak for the preview"
                      />
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                      <button
                        onClick={handleGenerateVoice}
                        disabled={isGeneratingAudio}
                        title="Generate and preview the selected voice"
                        className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-lg transition-colors disabled:bg-gray-600 disabled:cursor-wait w-full">
                        {isGeneratingAudio
                          ? 'Génération...'
                          : 'Générer & Pré-écouter'}
                      </button>
                      {audioUrl && (
                        <audio
                          src={audioUrl}
                          controls
                          className="h-8 w-full"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-8 transition-opacity duration-300" onClick={() => setPreviewImage(null)}>
          <button className="absolute top-6 right-6 text-white/70 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all">
            <XMarkIcon className="w-8 h-8" />
          </button>
          <img
            src={`data:${previewImage.type};base64,${previewImage.base64}`}
            alt={previewImage.label}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl ring-1 ring-white/10"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 text-white font-medium shadow-xl">
            {previewImage.label}
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterManager;